import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

/**
 * PageTransition — انيميشن احترافي بسيط ومميز ومش مزعج
 * يطبق على تحميل كل حاجة في الموقع ماعدا الهيرو (الهيرو له أنيميشن خاص)
 *
 * - fade + تحريك 8px فقط (أهدأ بكتير من 24px)
 * - 380ms + spring soft (stiffness 260, damping 26)
 * - يحترم prefers-reduced-motion تلقائياً
 * - يستثني الصفحة الرئيسية '/' لأن الهيرو فيها له reveal خاص
 * - لا يعمل على أول تحميل (initial=false) لتجنب flash مع SSR
 */
export function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const reducedMotion = useReducedMotion()

  // الصفحة الرئيسية: الهيرو له أنيميشن خاص → نعرض بدون transition خارجي
  const isHome = location.pathname === '/'
  if (isHome || reducedMotion) {
    return <>{children}</>
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : {
                duration: 0.38,
                ease: [0.16, 1, 0.3, 1],
              }
        }
        // will-change يتحكم فيه framer تلقائياً
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * SectionReveal — wrapper خفيف لأي قسم بعد الهيرو
 * يستخدم نفس الـ Reveal CSS لكن مع framer-motion كبديل لو حابب
 * تحكم أدق. افتراضياً يعتمد على CSS reveal-init / reveal-show.
 * هذا الكومبوننت يضيف stagger تلقائي للأطفال.
 */
export function SectionFade({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode
  delay?: number
  className?: string
}) {
  const reducedMotion = useReducedMotion()
  if (reducedMotion) return <div className={className}>{children}</div>

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -8% 0px', amount: 0.15 }}
      transition={{
        duration: 0.6,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}
